<template>
  <section>
    <b-modal
      v-if="referenceType === 'employer'"
      id="reference-employer"
      ref="reference-employer"
      data-cy="marketing-reference-employer-modal"
      :data-view-ready="!loading"
      no-close-on-backdrop>
      <template #modal-header="{ close }">
        <h5 class="modal-title">
          {{ $t('message.interestedParty.reference.employer.title') }}
        </h5>
        <button
          type="button"
          class="close"
          aria-label="Close"
          data-cy="marketing-reference-employer-close-button"
          @click="close()">
          ×
        </button>
      </template>
      <div
        class="row">
        <div
          v-if="!employerReferenceContacts.length < 1"
          class="col-sm-12 col-md-12 mb-2">
          <coozzy-button
            v-for="item in employerReferenceContacts"
            :key="item.id"
            :design="item.id === contactId ? 'primary' : (isEmpty(item.id, 'employer') ? 'default' : 'prop-green')"
            class="mr-2 mb-2"
            @click="showRequester(item.id)">
            {{ item.name }}
          </coozzy-button>
        </div>
      </div>
      <div
        class="row">
        <div class="col-12">
          <div class="row">
            <div class="col-sm-6 col-md-6 mb-2">
              <coozzy-form-input
                :key="contactId"
                v-model="employerObtainedOn"
                data-cy="marketing-reference-employer-obtained-on"
                type="date"
                :name="$t('message.interestedParty.reference.obtainedOn') + ':'"
                @blur="dateToObject('employer')" />
            </div>
          </div>
        </div>
        <div class="col-sm-6 col-md-6 mb-2">
          <coozzy-form-input
            v-model="employerReference.name"
            data-cy="marketing-reference-employer-employer"
            :name="$t('message.interestedParty.reference.employer.employer')" />
        </div>
        <div class="col-sm-6 col-md-6 mb-2">
          <coozzy-form-input
            v-model="employerReference.contactPerson"
            data-cy="marketing-reference-employer-contact-person"
            :name="$t('message.interestedParty.reference.employer.contactPerson')" />
        </div>
        <div class="col-sm-6 col-md-6 mb-2">
          <coozzy-form-input
            v-model="employerReference.email"
            data-cy="marketing-reference-employer-email"
            :name="$t('message.interestedParty.reference.employer.email')" />
        </div>
        <div class="col-sm-6 col-md-6 mb-2">
          <coozzy-form-input
            v-model="employerReference.phone"
            data-cy="marketing-reference-employer-phone"
            :name="$t('message.interestedParty.reference.employer.phone')" />
        </div>
        <div class="col-sm-6 col-md-6 mb-2">
          <label for="duration">{{ $t('message.interestedParty.reference.employer.duration') }}</label>
          <coozzy-form-textarea
            id="duration"
            v-model="employerReference.duration"
            data-cy="marketing-reference-employer-duration"
            :text="employerReference.duration"
            :initial="employerReference.duration"
            :show-label="false"
            :rows="3">
            {{ employerReference.duration }}
          </coozzy-form-textarea>
        </div>
        <div class="col-sm-6 col-md-6 mb-2">
          <label for="probation">{{ $t('message.interestedParty.reference.employer.probation') }}</label>
          <coozzy-form-textarea
            id="probation"
            v-model="employerReference.probation"
            data-cy="marketing-reference-employer-probation"
            :text="employerReference.probation"
            :initial="employerReference.probation"
            :show-label="false"
            :rows="3">
            {{ employerReference.probation }}
          </coozzy-form-textarea>
        </div>
        <div class="col-sm-6 col-md-6 mb-2">
          <label for="temporalEmployment">
            {{ $t('message.interestedParty.reference.employer.temporalEmployment') }}
          </label>
          <coozzy-form-textarea
            id="temporalEmployment"
            v-model="employerReference.temporalEmployment"
            data-cy="marketing-reference-employer-temporal"
            :text="employerReference.temporalEmployment"
            :initial="employerReference.temporalEmployment"
            :show-label="false"
            :rows="3">
            {{ employerReference.temporalEmployment }}
          </coozzy-form-textarea>
        </div>
        <div class="col-sm-6 col-md-6 mb-2">
          <label for="salary">{{ $t('message.interestedParty.reference.employer.salary') }}</label>
          <coozzy-form-textarea
            id="salary"
            v-model="employerReference.salary"
            data-cy="marketing-reference-employer-salary"
            :text="employerReference.salary"
            :initial="employerReference.salary"
            :show-label="false"
            :rows="3">
            {{ employerReference.salary }}
          </coozzy-form-textarea>
        </div>
        <div class="col-sm-12 col-md-12 mb-2">
          <label for="notes">{{ $t('message.interestedParty.reference.employer.notes') }}</label>
          <coozzy-form-textarea
            id="notes"
            v-model="employerReference.notes"
            data-cy="marketing-reference-employer-remarks"
            :text="employerReference.notes"
            :initial="employerReference.notes"
            :show-label="false"
            :rows="3">
            {{ employerReference.notes }}
          </coozzy-form-textarea>
        </div>
      </div>
      <template #modal-footer>
        <div class="d-flex w-100 justify-content-between align-items-center">
          <div>
            <coozzy-button
              data-cy="marketing-reference-employer-cancel-button"
              design="default"
              :disabled="loading"
              @click="$bvModal.hide('reference-employer')">
              {{ $t('message.generic.cancel') }}
            </coozzy-button>
            <coozzy-button
              data-cy="marketing-reference-employer-save-draft-button"
              design="green"
              class="ml-2"
              :disabled="loading"
              @click="saveEmployerReference(false)">
              {{ $t('message.marketingObjects.editAdvert.saveDraft') }}
            </coozzy-button>
          </div>
          <div>
            <coozzy-button
              data-cy="marketing-reference-employer-finish-button"
              design="prop-green"
              class="mr-2"
              :disabled="loading"
              @click="onFinishEmployerClicked">
              {{ $t('message.interestedParty.reference.manuallyFinishReference') }}
            </coozzy-button>
            <coozzy-button
              data-cy="marketing-reference-employer-send-button"
              design="primary"
              :disabled="loading"
              @click="onSendEmployerClicked">
              {{ $t('message.interestedParty.reference.send') }}
            </coozzy-button>
          </div>
        </div>
      </template>
    </b-modal>
    <b-modal
      v-if="referenceType === 'landlord'"
      id="reference-landlord"
      ref="reference-landlord"
      data-cy="marketing-reference-landlord-modal"
      :data-view-ready="!loading"
      no-close-on-backdrop>
      <template #modal-header="{ close }">
        <h5 class="modal-title">
          {{ $t('message.interestedParty.reference.landlord.title') }}
        </h5>
        <button
          type="button"
          class="close"
          aria-label="Close"
          data-cy="marketing-reference-landlord-close-button"
          @click="close()">
          ×
        </button>
      </template>
      <div
        class="row">
        <div
          v-if="!landlordRequesterContacts.length < 1"
          class="col-sm-12 col-md-12 mb-2">
          <coozzy-button
            v-for="item in landlordRequesterContacts"
            :key="item.id"
            :design="item.id === contactId ? 'primary' : isEmpty(item.id, 'landlord') ? 'default' : 'prop-green'"
            class="mr-2 mb-2"
            @click="showRequester(item.id)">
            {{ item.name }}
          </coozzy-button>
        </div>
      </div>
      <div
        class="row">
        <div class="col-12">
          <div class="row">
            <div class="col-sm-6 col-md-6 mb-2">
              <coozzy-form-input
                :key="contactId"
                v-model="landlordObtainedOn"
                data-cy="marketing-reference-landlord-obtained-on"
                type="date"
                :name="$t('message.interestedParty.reference.obtainedOn') + ':'"
                @blur="dateToObject('landlord')" />
            </div>
          </div>
        </div>
        <div class="col-sm-6 col-md-6 mb-2">
          <coozzy-form-input
            v-model="landlordReference.name"
            data-cy="marketing-reference-landlord-landlord"
            :name="$t('message.interestedParty.reference.landlord.landlord')" />
        </div>
        <div class="col-sm-6 col-md-6 mb-2">
          <coozzy-form-input
            v-model="landlordReference.contactPerson"
            data-cy="marketing-reference-landlord-contact-person"
            :name="$t('message.interestedParty.reference.landlord.contactPerson')" />
        </div>
        <div class="col-sm-6 col-md-6 mb-2">
          <coozzy-form-input
            v-model="landlordReference.email"
            data-cy="marketing-reference-landlord-email"
            :name="$t('message.interestedParty.reference.landlord.email')" />
        </div>
        <div class="col-sm-6 col-md-6 mb-2">
          <coozzy-form-input
            v-model="landlordReference.phone"
            data-cy="marketing-reference-landlord-phone"
            :name="$t('message.interestedParty.reference.landlord.phone')" />
        </div>
        <div
          class="col-sm-6 col-md-6 mb-2">
          <label for="durationLandlord">{{ $t('message.interestedParty.reference.landlord.duration') }}</label>
          <coozzy-form-textarea
            id="durationLandlord"
            v-model="landlordReference.duration"
            data-cy="marketing-reference-landlord-duration"
            :text="landlordReference.duration"
            :initial="landlordReference.duration"
            :show-label="false"
            :rows="3">
            {{ landlordReference.duration }}
          </coozzy-form-textarea>
        </div>
        <div
          class="col-sm-6 col-md-6 mb-2">
          <label for="punctualPayment">{{ $t('message.interestedParty.reference.landlord.punctualPayment') }}</label>
          <coozzy-form-textarea
            id="punctualPayment"
            v-model="landlordReference.punctualPayment"
            data-cy="marketing-reference-landlord-rent-on-time"
            :text="landlordReference.punctualPayment"
            :initial="landlordReference.punctualPayment"
            :show-label="false"
            :rows="3">
            {{ landlordReference.punctualPayment }}
          </coozzy-form-textarea>
        </div>
        <div
          class="col-sm-6 col-md-6 mb-2">
          <label for="complaints">{{ $t('message.interestedParty.reference.landlord.complaints') }}</label>
          <coozzy-form-textarea
            id="complaints"
            v-model="landlordReference.complaints"
            data-cy="marketing-reference-landlord-complaints"
            :text="landlordReference.complaints"
            :initial="landlordReference.complaints"
            :show-label="false"
            :rows="3">
            {{ landlordReference.complaints }}
          </coozzy-form-textarea>
        </div>
        <div
          class="col-sm-6 col-md-6 mb-2">
          <label for="costs">{{ $t('message.interestedParty.reference.landlord.costs') }}</label>
          <coozzy-form-textarea
            id="costs"
            v-model="landlordReference.costs"
            data-cy="marketing-reference-landlord-apartment-cost"
            :text="landlordReference.costs"
            :initial="landlordReference.costs"
            :show-label="false"
            :rows="3">
            {{ landlordReference.costs }}
          </coozzy-form-textarea>
        </div>
        <div
          class="col-sm-12 col-md-12 mb-2">
          <label for="notesLandlord">{{ $t('message.interestedParty.reference.landlord.notes') }}</label>
          <coozzy-form-textarea
            id="notesLandlord"
            v-model="landlordReference.notes"
            data-cy="marketing-reference-landlord-remarks"
            :text="landlordReference.notes"
            :initial="landlordReference.notes"
            :show-label="false"
            :rows="3">
            {{ landlordReference.notes }}
          </coozzy-form-textarea>
        </div>
      </div>
      <template #modal-footer>
        <div class="d-flex w-100 justify-content-between align-items-center">
          <div>
            <coozzy-button
              data-cy="marketing-reference-landlord-cancel-button"
              design="default"
              :disabled="loading"
              @click="$bvModal.hide('reference-landlord')">
              {{ $t('message.generic.cancel') }}
            </coozzy-button>
            <coozzy-button
              data-cy="marketing-reference-landlord-save-draft-button"
              design="green"
              class="ml-2"
              :disabled="loading"
              @click="saveLandlordReference(false)">
              {{ $t('message.marketingObjects.editAdvert.saveDraft') }}
            </coozzy-button>
          </div>
          <div>
            <coozzy-button
              data-cy="marketing-reference-landlord-finish-button"
              design="prop-green"
              class="mr-2"
              :disabled="loading"
              @click="onFinishLandlordClicked">
              {{ $t('message.interestedParty.reference.manuallyFinishReference') }}
            </coozzy-button>
            <coozzy-button
              data-cy="marketing-reference-landlord-send-button"
              design="primary"
              :disabled="loading"
              @click="onSendLandlordClicked">
              {{ $t('message.interestedParty.reference.send') }}
            </coozzy-button>
          </div>
        </div>
      </template>
    </b-modal>
    <b-modal
      id="reference-resend-confirmation"
      ref="reference-resend-confirmation"
      data-cy="marketing-reference-resend-confirmation-modal"
      no-close-on-backdrop
      hide-footer
      :title="$t('message.interestedParty.reference.resendTitle')">
      <div class="col p-0">
        <p>{{ $t('message.interestedParty.reference.resendBody') }}</p>
        <coozzy-button
          data-cy="marketing-reference-resend-cancel-button"
          size="small"
          design="default"
          :disabled="loading"
          @click="$bvModal.hide('reference-resend-confirmation')">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
        <coozzy-button
          data-cy="marketing-reference-resend-confirm-button"
          size="small"
          class="float-right mb-0"
          design="prop-green"
          :disabled="loading"
          @click="confirmResendReference">
          {{ $t('message.interestedParty.reference.send') }}
        </coozzy-button>
      </div>
    </b-modal>
    <b-modal
      id="reference-manual-finish-confirmation"
      ref="reference-manual-finish-confirmation"
      data-cy="marketing-reference-manual-finish-confirmation-modal"
      no-close-on-backdrop
      hide-footer
      :title="$t('message.interestedParty.reference.manualFinishConfirmTitle')">
      <div class="col p-0">
        <p>{{ $t('message.interestedParty.reference.manualFinishConfirmBody') }}</p>
        <coozzy-button
          data-cy="marketing-reference-manual-finish-cancel-button"
          size="small"
          design="default"
          :disabled="loading"
          @click="$bvModal.hide('reference-manual-finish-confirmation')">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
        <coozzy-button
          data-cy="marketing-reference-manual-finish-confirm-button"
          size="small"
          class="float-right mb-0"
          design="prop-green"
          :disabled="loading"
          @click="confirmManualFinishReference">
          {{ $t('message.generic.confirm') }}
        </coozzy-button>
      </div>
    </b-modal>
  </section>
</template>
<script>
import RequestApi from '@/marketingBasic/api/RequestApi'
import Vue from 'vue'
import CoozzyButton from '@/framework/components/button/CoozzyButton'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput'
import CoozzyFormTextarea from '@/framework/components/form/textarea/CoozzyFormTextarea'
import { isEmailValid } from '@/mixins/validation'
import { user } from '@/mixins/user'
import { loadReferenceCheckSettings } from '@/settings/marketing/referenceCheckSettings'

export default {
  name: 'ReferenceModal',
  components: {
    CoozzyFormTextarea,
    CoozzyFormInput,
    CoozzyButton
  },
  mixins: [user],
  props: {
    request: {
      type: Object,
      default: null
    },
    object: {
      type: Object,
      default: null
    },
    referenceType: {
      type: String,
      default: ''
    },
    requesterContacts: {
      type: Array,
      default() {
        return []
      }
    }
  },
  data() {
    return {
      contactId: '',
      employerReferences: {},
      landlordReferences: {},
      employerReference: {
        contactPerson: '',
        duration: '',
        name: '',
        notes: '',
        probation: '',
        salary: '',
        temporalEmployment: '',
        email: '',
        phone: '',
        finalized: false,
        obtainedOn: null,
        emailRequestedAt: '0',
        publicId: ''
      },
      landlordReference: {
        complaints: '',
        contactPerson: '',
        costs: '',
        duration: '',
        name: '',
        notes: '',
        punctualPayment: '',
        email: '',
        phone: '',
        finalized: false,
        obtainedOn: null,
        emailRequestedAt: '0',
        publicId: ''
      },
      loading: false,
      employerObtainedOn: null,
      landlordObtainedOn: null,
      modalShowHandler: null,
      pendingResendType: '',
      pendingManualFinishType: ''
    }
  },
  computed: {
    isParkingOrSecondaryObject() {
      const category = (this.object?.category || '').toString().toLowerCase()
      return category === 'parking_space' || category === 'secondary_room'
    },
    employerReferenceEntries() {
      const requesters = this.request?.requesters || []
      if (this.isParkingOrSecondaryObject) {
        return requesters
      }
      return requesters.concat(this.request?.guarantors || [])
    },
    employerReferenceContacts() {
      if (!this.request || !this.requesterContacts) {
        return []
      }
      const employerContactIdSet = new Set(this.employerReferenceEntries.map(entry => entry.contactId))
      return this.requesterContacts.filter(contact => employerContactIdSet.has(contact.id))
    },
    landlordReferenceEntries() {
      const requesters = this.request?.requesters || []
      if (this.isParkingOrSecondaryObject) {
        return requesters
      }
      return requesters.concat(this.request?.guarantors || [])
    },
    landlordRequesterContacts() {
      if (!this.request || !this.requesterContacts) {
        return []
      }
      const landlordContactIdSet = new Set(this.landlordReferenceEntries.map(entry => entry.contactId))
      return this.requesterContacts.filter(contact => landlordContactIdSet.has(contact.id))
    },
    contactsForCurrentReference() {
      if (this.referenceType === 'landlord') {
        return this.landlordRequesterContacts
      }
      return this.employerReferenceContacts
    }
    // checkEmpty() {
    //   if (this.referenceType === 'landlord') {
    //     const landl = this.request.requesters.filter(o => o.landlordReference !== null)
    //     const pp = landl.map(land => land.landlordReference.complaints !== '' || land.landlordReference.contactPerson !== '' || land.landlordReference.costs !== '' || land.landlordReference.duration !== '' || land.landlordReference.name !== '' || land.landlordReference.notes !== '' || land.landlordReference.punctualPayment !== '').filter(x => x === true)
    //     return pp.length > 0
    //   } else if (this.referenceType === 'employer') {
    //     const emp = this.request.requesters.filter(o => o.employerReference !== null)
    //     const pp = emp.map(empl => empl.employerReference.contactPerson !== '' || empl.employerReference.duration !== '' || empl.employerReference.name !== '' || empl.employerReference.notes !== '' || empl.employerReference.probation !== '' || empl.employerReference.salary !== '' || empl.employerReference.temporalEmployment !== '').filter(x => x === true)
    //     return pp.length > 0
    //   }
    //   return false
    // }
  },
  watch: {
    landlordReference: function () {
      if (this.contactId !== '') {
        this.landlordReferences[this.contactId] = this.landlordReference
      }
    },
    employerReference: function () {
      if (this.contactId !== '') {
        this.employerReferences[this.contactId] = this.employerReference
      }
    }
  },
  mounted: function () {
    this.modalShowHandler = (bvEvent, modalId) => {
      if (modalId === 'reference-employer' || modalId === 'reference-landlord') {
        // We need at least 1 contact
        if (this.contactsForCurrentReference.length === 0) {
          this.hide()
          return
        }

        // Store references of all requesters and guarantors
        const allEntries = (this.request.requesters || []).concat(this.request.guarantors || [])
        for (const entry of allEntries) {
          if (entry.landlordReference) {
            this.landlordReferences[entry.contactId] = entry.landlordReference
          } else {
            this.landlordReferences[entry.contactId] = {
              complaints: '',
              contactPerson: '',
              costs: '',
              duration: '',
              name: '',
              notes: '',
              punctualPayment: '',
              email: '',
              phone: '',
              finalized: false,
              emailRequestedAt: '0',
              publicId: ''
            }
          }
          if (entry.employerReference) {
            this.employerReferences[entry.contactId] = entry.employerReference
          } else {
            this.employerReferences[entry.contactId] = {
              contactPerson: '',
              duration: '',
              name: '',
              notes: '',
              probation: '',
              salary: '',
              temporalEmployment: '',
              email: '',
              phone: '',
              finalized: false,
              emailRequestedAt: '0',
              publicId: ''
            }
          }
        }

        this.showRequester(this.contactsForCurrentReference[0].id)
      }
    }
    this.$root.$on('bv::modal::show', this.modalShowHandler)
  },
  beforeDestroy() {
    if (this.modalShowHandler) {
      this.$root.$off('bv::modal::show', this.modalShowHandler)
    }
  },
  methods: {
    dateToObject(value) {
      if (value === 'employer') {
        if (this.employerObtainedOn !== null && this.employerObtainedOn !== '') {
          const employerObtainedOn = new Date(this.employerObtainedOn)
          this.employerReference.obtainedOn = {
            year: employerObtainedOn.getFullYear(),
            month: employerObtainedOn.getMonth() + 1,
            day: employerObtainedOn.getDate()
          }
        } else {
          this.employerReference.obtainedOn = null
        }
      } else if (value === 'landlord') {
        if (this.landlordObtainedOn !== null && this.landlordObtainedOn !== '') {
          const landlordObtainedOn = new Date(this.landlordObtainedOn)
          this.landlordReference.obtainedOn = {
            year: landlordObtainedOn.getFullYear(),
            month: landlordObtainedOn.getMonth() + 1,
            day: landlordObtainedOn.getDate()
          }
        } else {
          this.landlordReference.obtainedOn = null
        }
      }
    },
    getDatesFromObject () {
      this.employerObtainedOn = this.employerReference.obtainedOn ? `${this.employerReference.obtainedOn.year}-${this.employerReference.obtainedOn.month.toString().padStart(2, '0')}-${this.employerReference.obtainedOn.day.toString().padStart(2, '0')}` : null
      this.landlordObtainedOn = this.landlordReference.obtainedOn ? `${this.landlordReference.obtainedOn.year}-${this.landlordReference.obtainedOn.month.toString().padStart(2, '0')}-${this.landlordReference.obtainedOn.day.toString().padStart(2, '0')}` : null
    },
    finishLandlordReference() {
      if (!this.landlordReference.finalized) {
        this.landlordReference.finalized = true
      }
      const hadEmailRequest = this.hasEmailBeenRequested(this.landlordReference)
      this.saveLandlordReference(true, hadEmailRequest)
      this.$emit('new-landlord-references', this.landlordReferences)
    },
    finishEmployerReference() {
      if (!this.employerReference.finalized) {
        this.employerReference.finalized = true
      }
      const hadEmailRequest = this.hasEmailBeenRequested(this.employerReference)
      this.saveEmployerReference(true, hadEmailRequest)
      this.$emit('new-employer-references', this.employerReferences)
    },
    onFinishLandlordClicked() {
      if (this.hasEmailBeenRequested(this.landlordReference)) {
        this.pendingManualFinishType = 'landlord'
        this.$bvModal.show('reference-manual-finish-confirmation')
        return
      }
      this.finishLandlordReference()
    },
    onFinishEmployerClicked() {
      if (this.hasEmailBeenRequested(this.employerReference)) {
        this.pendingManualFinishType = 'employer'
        this.$bvModal.show('reference-manual-finish-confirmation')
        return
      }
      this.finishEmployerReference()
    },
    confirmManualFinishReference() {
      this.$bvModal.hide('reference-manual-finish-confirmation')
      if (this.pendingManualFinishType === 'landlord') {
        this.finishLandlordReference()
      } else if (this.pendingManualFinishType === 'employer') {
        this.finishEmployerReference()
      }
      this.pendingManualFinishType = ''
    },
    hasDigitalReferenceRequest(reference) {
      return Number(reference?.emailRequestedAt || 0) > 0 ||
        Boolean(reference?.publicId && String(reference.publicId).trim())
    },
    hasEmailBeenRequested(reference) {
      return this.hasDigitalReferenceRequest(reference)
    },
    isReferenceEmailValid(email) {
      return Boolean(email && String(email).trim() && isEmailValid(String(email).trim()))
    },
    resolveReferenceCheckAccountId() {
      const marketingAccountId = this.object?.marketingAccountId
      if (marketingAccountId && String(marketingAccountId) !== '-1') {
        return String(marketingAccountId)
      }
      return this.accountId
    },
    ensureReferenceSenderConfigured() {
      const accountId = this.resolveReferenceCheckAccountId()
      if (!accountId || String(accountId) === '-1') {
        Vue.toasted.show(this.$t('message.interestedParty.reference.missingSenderEmail'), { type: 'error' })
        return Promise.resolve(false)
      }
      return loadReferenceCheckSettings(String(accountId), this.currentLanguage)
        .then(settings => {
          if (!settings.senderMailboxId) {
            Vue.toasted.show(this.$t('message.interestedParty.reference.missingSenderEmail'), { type: 'error' })
            return false
          }
          return true
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.generic'), { type: 'error' })
          return false
        })
    },
    onSendEmployerClicked() {
      if (!this.isReferenceEmailValid(this.employerReference.email)) {
        Vue.toasted.show(this.$t('message.interestedParty.reference.missingSupervisorEmail'), { type: 'error' })
        return
      }
      if (this.hasEmailBeenRequested(this.employerReference)) {
        this.pendingResendType = 'employer'
        this.$bvModal.show('reference-resend-confirmation')
        return
      }
      this.sendEmployerReferenceByEmail()
    },
    onSendLandlordClicked() {
      if (!this.isReferenceEmailValid(this.landlordReference.email)) {
        Vue.toasted.show(this.$t('message.interestedParty.reference.missingLandlordEmail'), { type: 'error' })
        return
      }
      if (this.hasEmailBeenRequested(this.landlordReference)) {
        this.pendingResendType = 'landlord'
        this.$bvModal.show('reference-resend-confirmation')
        return
      }
      this.sendLandlordReferenceByEmail()
    },
    confirmResendReference() {
      this.$bvModal.hide('reference-resend-confirmation')
      if (this.pendingResendType === 'landlord') {
        this.sendLandlordReferenceByEmail()
      } else {
        this.sendEmployerReferenceByEmail()
      }
      this.pendingResendType = ''
    },
    sendEmployerReferenceByEmail() {
      if (!this.contactId || !this.request) {
        return
      }
      this.loading = true
      this.ensureReferenceSenderConfigured()
        .then(configured => {
          if (!configured) {
            return
          }
          this.employerReferences[this.contactId] = this.employerReference
          return RequestApi.setEmployerReference(
            this.employerReference,
            this.request.id,
            this.contactId,
            false,
            this.object.marketingLanguage
          )
            .then(() => RequestApi.requestEmployerReferenceByEmail(
              this.request.id,
              this.contactId,
              this.object.marketingLanguage
            ))
            .then(response => {
              const updated = response?.employerReference
              if (updated) {
                this.employerReference = Object.assign({}, this.employerReference, updated)
                this.employerReferences[this.contactId] = this.employerReference
                const entry = this.employerReferenceEntries.find(e => e.contactId === this.contactId)
                if (entry) {
                  entry.employerReference = this.employerReference
                }
              }
              this.$emit('new-employer-references', this.employerReferences)
              Vue.toasted.show(this.$t('message.successMessages.requestEmployerReferenceByEmail'), { type: 'success' })
              this.hide()
            })
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.savingErrors.requestEmployerReferenceByEmail'), { type: 'error' })
        })
        .finally(() => {
          this.loading = false
        })
    },
    sendLandlordReferenceByEmail() {
      if (!this.contactId || !this.request) {
        return
      }
      this.loading = true
      this.ensureReferenceSenderConfigured()
        .then(configured => {
          if (!configured) {
            return
          }
          this.landlordReferences[this.contactId] = this.landlordReference
          return RequestApi.setLandlordReference(
            this.landlordReference,
            this.request.id,
            this.contactId,
            false,
            this.object.marketingLanguage
          )
            .then(() => RequestApi.requestLandlordReferenceByEmail(
              this.request.id,
              this.contactId,
              this.object.marketingLanguage
            ))
            .then(response => {
              const updated = response?.landlordReference
              if (updated) {
                this.landlordReference = Object.assign({}, this.landlordReference, updated)
                this.landlordReferences[this.contactId] = this.landlordReference
                const entry = this.landlordReferenceEntries.find(e => e.contactId === this.contactId)
                if (entry) {
                  entry.landlordReference = this.landlordReference
                }
              }
              this.$emit('new-landlord-references', this.landlordReferences)
              Vue.toasted.show(this.$t('message.successMessages.requestLandlordReferenceByEmail'), { type: 'success' })
              this.hide()
            })
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.savingErrors.requestLandlordReferenceByEmail'), { type: 'error' })
        })
        .finally(() => {
          this.loading = false
        })
    },
    getRequester(contactId) {
      if (!this.request) return null
      const allEntries = (this.request.requesters || []).concat(this.request.guarantors || [])
      return allEntries.find(r => r.contactId === contactId) || null
    },
    show() {
      if (this.referenceType === 'landlord') {
        this.$refs['reference-landlord'].show()
      } else {
        this.$refs['reference-employer']?.show()
      }
    },
    hide() {
      if (this.referenceType === 'landlord') {
        if (this.$refs['reference-landlord']) {
          this.$refs['reference-landlord'].hide()
        }
      } else {
        if (this.$refs['reference-employer']) {
          this.$refs['reference-employer'].hide()
        }
      }
    },
    showRequester(contactId) {
      if (this.contactId !== contactId) {
        this.contactId = contactId
        if (this.isEmpty(contactId, 'landlord')) {
          this.landlordReferences[contactId] = {
            complaints: '',
            contactPerson: '',
            costs: '',
            duration: '',
            name: '',
            notes: '',
            punctualPayment: '',
            email: '',
            phone: '',
            finalized: false,
            emailRequestedAt: '0',
            publicId: ''
          }
        }
        if (this.isEmpty(contactId, 'employer')) {
          this.employerReferences[contactId] = {
            contactPerson: '',
            duration: '',
            name: '',
            notes: '',
            probation: '',
            salary: '',
            temporalEmployment: '',
            email: '',
            phone: '',
            finalized: false,
            emailRequestedAt: '0',
            publicId: ''
          }
        }
        this.landlordReference = this.landlordReferences[contactId]
        this.employerReference = this.employerReferences[contactId]
        this.getDatesFromObject()
      }
    },
    isEmpty(contactId, type) {
      if (type === 'landlord') {
        if (this.landlordReferences[contactId] === undefined) {
          return true
        }
        if (this.hasDigitalReferenceRequest(this.landlordReferences[contactId])) {
          return false
        }

        return !(this.landlordReferences[contactId].complaints !== '' ||
          this.landlordReferences[contactId].contactPerson !== '' ||
          (this.landlordReferences[contactId].costs !== '' && this.landlordReferences[contactId].costs !== 'CHF -1') ||
          (this.employerReferences[contactId].duration !== '' &&
            this.employerReferences[contactId].duration !== '-') ||
          this.landlordReferences[contactId].name !== '' ||
          this.landlordReferences[contactId].notes !== '' ||
          this.landlordReferences[contactId].punctualPayment !== '' ||
          this.landlordReferences[contactId].email !== '' ||
          this.landlordReferences[contactId].phone !== '')
      } else if (type === 'employer') {
        if (this.employerReferences[contactId] === undefined) {
          return true
        }
        if (this.hasDigitalReferenceRequest(this.employerReferences[contactId])) {
          return false
        }

        return !(this.employerReferences[contactId].contactPerson !== '' ||
          (this.employerReferences[contactId].duration !== '' &&
          this.employerReferences[contactId].duration !== '-') ||
          this.employerReferences[contactId].name !== '' ||
          this.employerReferences[contactId].notes !== '' ||
          this.employerReferences[contactId].probation !== '' ||
          (this.employerReferences[contactId].salary !== '' &&
          this.employerReferences[contactId].salary !== '-') ||
          this.employerReferences[contactId].temporalEmployment !== '' ||
          this.employerReferences[contactId].email !== '' ||
          this.employerReferences[contactId].phone !== '')
      }
    },
    saveEmployerReference(generatePdf, finalizeAfterEmail = false) {
      this.loading = true
      const promises = []

      for (const entry of this.employerReferenceEntries) {
        if (!this.isEmpty(entry.contactId, 'employer')) {
          promises.push(
            RequestApi.setEmployerReference(
              this.employerReferences[entry.contactId],
              this.request.id,
              entry.contactId,
              generatePdf,
              this.object.marketingLanguage
            )
          )
        }
        entry.employerReference = this.employerReferences[entry.contactId]
      }

      Promise.all(promises)
        .then(() => {
          if (finalizeAfterEmail && this.contactId) {
            return RequestApi.setEmployerReferenceFinalized(this.request.id, this.contactId, true)
          }
          return null
        })
        .then(() => {
          this.$emit('refresh-documents')
          this.$refs['reference-employer']?.hide()
          Vue.toasted.show(this.$t('message.successMessages.setEmployerReference'), { type: 'success' })
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.savingErrors.setEmployerReference'), { type: 'error' })
        })
        .finally(() => {
          this.loading = false
        })
    },
    saveLandlordReference(generatePdf, finalizeAfterEmail = false) {
      this.loading = true
      const promises = []

      for (const entry of this.landlordReferenceEntries) {
        if (!this.isEmpty(entry.contactId, 'landlord')) {
          promises.push(
            RequestApi.setLandlordReference(
              this.landlordReferences[entry.contactId],
              this.request.id,
              entry.contactId,
              generatePdf,
              this.object.marketingLanguage
            )
          )
        }
        entry.landlordReference = this.landlordReferences[entry.contactId]
      }

      Promise.all(promises)
        .then(() => {
          if (finalizeAfterEmail && this.contactId) {
            return RequestApi.setLandlordReferenceFinalized(this.request.id, this.contactId, true)
          }
          return null
        })
        .then(() => {
          this.$emit('refresh-documents')
          this.$refs['reference-landlord']?.hide()
          Vue.toasted.show(this.$t('message.successMessages.setLandlordReference'), { type: 'success' })
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.savingErrors.setLandlordReference'), { type: 'error' })
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>
